/**
 * HTML cleanup + list shaping for product redemption_instructions and terms.
 * Shared by legacy ProductDetails and ProductDetailsRedesign (API: redemption_details).
 */

export type ProductRedemptionDetails = {
  redemption_channels?: string[] | null;
  redemption_instructions?: string | null;
  terms?: string | null;
  redemption_url?: string | null;
};

export function productHasRedemptionSection(
  rd: ProductRedemptionDetails | null | undefined
): boolean {
  if (!rd) return false;
  const channels = rd.redemption_channels;
  const hasChannels =
    Array.isArray(channels) &&
    channels.length > 0 &&
    channels.some((ch) => ch && String(ch).trim() !== "");
  const hasInstructions = !!(rd.redemption_instructions && rd.redemption_instructions.trim() !== "");
  const hasTerms = !!(rd.terms && rd.terms.trim() !== "");
  return hasChannels || hasInstructions || hasTerms;
}

function stripInlineStyles(htmlContent: string): string {
  if (!htmlContent) return htmlContent;

  let cleaned = htmlContent.replace(/style\s*=\s*["'][^"']*["']/gi, "");
  cleaned = cleaned.replace(/\s*style\s*=\s*["'][\s]*["']/gi, "");
  cleaned = cleaned.replace(/\s*aria-level\s*=\s*["'][^"']*["']/gi, "");
  cleaned = cleaned.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
  cleaned = cleaned.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

  return cleaned;
}

export function optimizeRedemptionInstructions(htmlContent: string): string {
  if (!htmlContent) return htmlContent;

  if (htmlContent.match(/<[ou]l[^>]*>/i)) {
    let cleaned = stripInlineStyles(htmlContent);

    cleaned = cleaned.replace(/<([ou]l|li)([^>]*)>/gi, (match, tag, attributes) => {
      const cleanedAttrs = attributes
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*aria-level\s*=\s*["'][^"']*["']/gi, "")
        .trim();
      return cleanedAttrs ? `<${tag} ${cleanedAttrs}>` : `<${tag}>`;
    });

    cleaned = cleaned.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (match, content) => {
      let cleanedContent = content
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
        .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

      cleanedContent = cleanedContent.replace(/&nbsp;/g, " ").trim();
      cleanedContent = cleanedContent.replace(/\s+/g, " ").trim();

      return `<li>${cleanedContent}</li>`;
    });

    cleaned = cleaned.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (match, content) => {
      let cleanedContent = content
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
        .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

      cleanedContent = cleanedContent.replace(/&nbsp;/g, " ").trim();
      cleanedContent = cleanedContent.replace(/\s+/g, " ").trim();

      if (cleanedContent && cleanedContent.length > 0) {
        return `<p>${cleanedContent}</p>`;
      }
      return "";
    });

    cleaned = cleaned.replace(/<p[^>]*>\s*<\/p>/gi, "");

    return cleaned;
  }

  let cleaned = stripInlineStyles(htmlContent);

  cleaned = cleaned.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
  cleaned = cleaned.replace(/<div[^>]*>\s*(&nbsp;|\s)*\s*<\/div>/gi, "");
  cleaned = cleaned.replace(/<p[^>]*>\s*(&nbsp;|\s)*\s*<\/p>/gi, "");

  const allItems: string[] = [];

  const divRegex = /<div[^>]*>([\s\S]*?)<\/div>/gi;
  let divMatch;
  while ((divMatch = divRegex.exec(cleaned)) !== null) {
    if (divMatch[1]) {
      let item = divMatch[1].trim();
      item = item.replace(/style\s*=\s*["'][^"']*["']/gi, "");
      item = item.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
      item = item.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
      item = item.replace(/&nbsp;/g, " ").trim();
      item = item.replace(/\s+/g, " ").trim();
      if (item && item.length > 0 && !/^(&nbsp;|\s)+$/.test(item)) {
        allItems.push(item);
      }
    }
  }

  if (allItems.length === 0) {
    const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
    let pMatch;
    while ((pMatch = pRegex.exec(cleaned)) !== null) {
      if (pMatch[1]) {
        let item = pMatch[1].trim();
        item = item.replace(/style\s*=\s*["'][^"']*["']/gi, "");
        item = item.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
        item = item.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
        item = item.replace(/&nbsp;/g, " ").trim();
        item = item.replace(/\s+/g, " ").trim();
        if (item && item.length > 0 && !/^(&nbsp;|\s)+$/.test(item)) {
          allItems.push(item);
        }
      }
    }
  }

  if (allItems.length > 1) {
    const listItems = allItems.map((item) => `<li>${item}</li>`).join("");
    return `<ol>${listItems}</ol>`;
  }

  if (allItems.length === 1) {
    return `<p>${allItems[0]}</p>`;
  }

  cleaned = cleaned.replace(/<div[^>]*>([\s\S]*?)<\/div>/gi, "<p>$1</p>");
  cleaned = cleaned.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (match, content) => {
    let text = content.replace(/style\s*=\s*["'][^"']*["']/gi, "");
    text = text.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
    text = text.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
    text = text.replace(/&nbsp;/g, " ").trim();
    text = text.replace(/\s+/g, " ").trim();
    if (text && text.length > 0) {
      return `<p>${text}</p>`;
    }
    return "";
  });

  return cleaned;
}

export function convertTermsToListItem(htmlContent: string): string {
  if (!htmlContent) return htmlContent;

  if (htmlContent.match(/<[ou]l[^>]*>/i)) {
    let cleaned = stripInlineStyles(htmlContent);

    cleaned = cleaned.replace(/<([ou]l|li)([^>]*)>/gi, (match, tag, attributes) => {
      const cleanedAttrs = attributes
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/\s*aria-level\s*=\s*["'][^"']*["']/gi, "")
        .trim();
      return cleanedAttrs ? `<${tag} ${cleanedAttrs}>` : `<${tag}>`;
    });

    cleaned = cleaned.replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, (match, content) => {
      let cleanedContent = content
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
        .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

      cleanedContent = cleanedContent.replace(/&nbsp;/g, " ").trim();
      cleanedContent = cleanedContent.replace(/\s+/g, " ").trim();

      return `<li>${cleanedContent}</li>`;
    });

    cleaned = cleaned.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (match, content) => {
      let cleanedContent = content
        .replace(/style\s*=\s*["'][^"']*["']/gi, "")
        .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
        .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");

      cleanedContent = cleanedContent.replace(/&nbsp;/g, " ").trim();
      cleanedContent = cleanedContent.replace(/\s+/g, " ").trim();

      if (cleanedContent && cleanedContent.length > 0) {
        return `<p>${cleanedContent}</p>`;
      }
      return "";
    });

    cleaned = cleaned.replace(/<p[^>]*>\s*<\/p>/gi, "");
    cleaned = cleaned.replace(/<p[^>]*>\s*<br[^>]*>\s*<\/p>/gi, "");

    return cleaned;
  }

  let cleanedContent = stripInlineStyles(htmlContent);
  const brTagRegex = /<br[^>]*>/gi;

  if (brTagRegex.test(cleanedContent)) {
    brTagRegex.lastIndex = 0;

    const hasParagraphTags = /<p[^>]*>/i.test(cleanedContent);

    let items: string[] = [];

    if (hasParagraphTags) {
      const pRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
      const allParagraphs: string[] = [];
      let pMatch;

      while ((pMatch = pRegex.exec(cleanedContent)) !== null) {
        if (pMatch[1]) {
          allParagraphs.push(pMatch[1]);
        }
      }

      allParagraphs.forEach((paraContent) => {
        if (brTagRegex.test(paraContent)) {
          brTagRegex.lastIndex = 0;

          const paraItems = paraContent
            .split(/<br[^>]*>/gi)
            .map((item) => {
              let cleanedItem = item.trim();
              cleanedItem = cleanedItem.replace(/style\s*=\s*["'][^"']*["']/gi, "");
              cleanedItem = cleanedItem.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
              cleanedItem = cleanedItem.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
              cleanedItem = cleanedItem.replace(/&nbsp;/g, " ").trim();
              cleanedItem = cleanedItem.replace(/\s+/g, " ").trim();
              return cleanedItem;
            })
            .filter((item) => item.length > 0 && !/^(&nbsp;|\s)+$/.test(item));

          items.push(...paraItems);
        } else {
          let cleanedPara = paraContent
            .replace(/style\s*=\s*["'][^"']*["']/gi, "")
            .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
            .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
          cleanedPara = cleanedPara.replace(/&nbsp;/g, " ").trim();
          cleanedPara = cleanedPara.replace(/\s+/g, " ").trim();

          if (cleanedPara && cleanedPara.length > 0) {
            items.push(cleanedPara);
          }
        }
      });
    } else {
      brTagRegex.lastIndex = 0;

      items = cleanedContent
        .split(/<br[^>]*>/gi)
        .map((item) => {
          let cleanedItem = item.trim();
          cleanedItem = cleanedItem.replace(/style\s*=\s*["'][^"']*["']/gi, "");
          cleanedItem = cleanedItem.replace(/<span[^>]*>(.*?)<\/span>/gi, "$1");
          cleanedItem = cleanedItem.replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
          cleanedItem = cleanedItem.replace(/&nbsp;/g, " ").trim();
          cleanedItem = cleanedItem.replace(/\s+/g, " ").trim();
          return cleanedItem;
        })
        .filter((item) => item.length > 0 && !/^(&nbsp;|\s)+$/.test(item));
    }

    if (items.length > 0) {
      let numberedCount = 0;

      items.forEach((item) => {
        const trimmedItem = item.trim();
        if (/^\d+\./.test(trimmedItem)) {
          numberedCount++;
        }
      });

      let hasNumberedItems = false;
      if (numberedCount >= 2) {
        hasNumberedItems = true;
      } else if (numberedCount === 1 && items.length === 1) {
        hasNumberedItems = true;
      }

      if (hasNumberedItems) {
        const listItems = items
          .map((item) => {
            let cleanedItem = item.replace(/^\d+\.\s*/, "").trim();
            return `<li>${cleanedItem}</li>`;
          })
          .join("");
        return `<ol>${listItems}</ol>`;
      }

      if (items.length > 1 && !/^\d+\./.test(items[0].trim()) && /^\d+\./.test(items[1].trim())) {
        const introText = items[0];
        const numberedItems = items
          .slice(1)
          .map((item) => {
            let cleanedItem = item.replace(/^\d+\.\s*/, "").trim();
            return `<li>${cleanedItem}</li>`;
          })
          .join("");
        return `<p>${introText}</p><ol>${numberedItems}</ol>`;
      }

      const listItems = items.map((item) => `<li>${item}</li>`).join("");
      return `<ul>${listItems}</ul>`;
    }
  }

  cleanedContent = cleanedContent.replace(/<p[^>]*>([\s\S]*?)<\/p>/gi, (match, content) => {
    let cleaned = content
      .replace(/style\s*=\s*["'][^"']*["']/gi, "")
      .replace(/<span[^>]*>(.*?)<\/span>/gi, "$1")
      .replace(/<font[^>]*>(.*?)<\/font>/gi, "$1");
    cleaned = cleaned.replace(/&nbsp;/g, " ").trim();
    cleaned = cleaned.replace(/\s+/g, " ").trim();

    if (cleaned && cleaned.length > 0) {
      return `<p>${cleaned}</p>`;
    }
    return "";
  });

  if (!/<[^>]+>/.test(cleanedContent) && cleanedContent.trim().length > 0) {
    return `<p>${cleanedContent.trim()}</p>`;
  }

  return cleanedContent;
}
